import { addDays, startOfDay } from "date-fns";
import { CalendarDays, ChevronRight } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { dayKey } from "../../mobile/mobileModel";
import { SOURCE_BAR, useAgendaEntries } from "../../mobile/useAgendaEntries";
import { todayTimeline } from "./homeModel";
import { useHomeFormat } from "./useHomeFormat";

/** Re-render every minute so the «now» marker moves. */
const useNow = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
};

/**
 * «Agenda de hoy»: today's timed items (events, tasks with a time…) on a
 * short timeline with the «now» marker; all-day items sit on top; the
 * footer previews tomorrow.
 */
export const TodayAgenda = () => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const now = useNow();
  const today = useMemo(() => startOfDay(new Date()), []);
  const tomorrow = useMemo(() => addDays(today, 1), [today]);
  const { entries, pending } = useAgendaEntries(today, tomorrow);
  const open = entries.filter((entry) => !entry.done);
  const { timed, allDay, nowIndex } = todayTimeline(open, now);
  const tomorrowCount = open.filter(
    (entry) => dayKey(entry.start) === dayKey(tomorrow),
  ).length;
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.agenda.${key}`, options);

  const nowMarker = (
    <li
      className="home-agenda__now"
      aria-label={t("now_label", { time: format.time(now) })}
    >
      <span>
        {t("now")} · {format.time(now)}
      </span>
    </li>
  );

  return (
    <section
      className="home-card home-agenda"
      aria-labelledby="home-agenda-title"
    >
      <header className="home-card__head">
        <h2 id="home-agenda-title" className="home-card__title">
          {t("title")}
          <span className="home-card__subtitle">{format.shortDate(now)}</span>
        </h2>
        <Link to="/agenda?view=week" className="home-pill-link">
          {t("week")}
          <ChevronRight aria-hidden className="size-3.5" strokeWidth={2} />
        </Link>
      </header>
      {allDay.length > 0 ? (
        <ul className="home-agenda__allday" aria-label={t("all_day")}>
          {allDay.slice(0, 3).map((entry) => (
            <li key={entry.id}>
              <span
                className={`home-agenda__bar ${SOURCE_BAR[entry.source] ?? "bg-muted-foreground"}`}
                aria-hidden
              />
              {entry.href ? (
                <Link to={entry.href}>{entry.title}</Link>
              ) : (
                <span>{entry.title}</span>
              )}
            </li>
          ))}
          {allDay.length > 3 ? (
            <li className="home-agenda__more">
              {t("more_all_day", { smart_count: allDay.length - 3 })}
            </li>
          ) : null}
        </ul>
      ) : null}
      {pending ? (
        <div className="home-inbox__loading" aria-busy="true">
          <span />
          <span />
        </div>
      ) : timed.length === 0 ? (
        <p className="home-agenda__empty">{t("empty")}</p>
      ) : (
        <ol className="home-agenda__list">
          {timed.map((entry, index) => (
            <AgendaSlot
              key={entry.id}
              marker={index === nowIndex ? nowMarker : null}
              time={format.time(entry.start)}
              title={entry.title}
              href={entry.href}
              bar={SOURCE_BAR[entry.source] ?? "bg-muted-foreground"}
              current={
                entry.start <= now &&
                (entry.end ?? new Date(entry.start.getTime() + 30 * 60_000)) >
                  now
              }
              kind={translate(`desk.home.agenda.sources.${entry.source}`, {
                _: "",
              })}
            />
          ))}
          {nowIndex === timed.length ? nowMarker : null}
        </ol>
      )}
      <footer className="home-agenda__foot">
        <span>
          <CalendarDays aria-hidden className="size-3.5" strokeWidth={1.75} />
          {t("tomorrow", { smart_count: tomorrowCount })}
        </span>
        <Link to="/agenda" className="home-link">
          {t("see_all")}
        </Link>
      </footer>
    </section>
  );
};

const AgendaSlot = ({
  marker,
  time,
  title,
  href,
  bar,
  current,
  kind,
}: {
  marker: React.ReactNode;
  time: string;
  title: string;
  href: string | null;
  bar: string;
  current: boolean;
  kind: string;
}) => (
  <>
    {marker}
    <li className="home-agenda__slot" data-current={current || undefined}>
      <span className="home-agenda__time">{time}</span>
      <span className="home-agenda__item">
        <span className={`home-agenda__bar ${bar}`} aria-hidden />
        <span className="min-w-0">
          {href ? (
            <Link to={href} className="home-agenda__title">
              {title}
            </Link>
          ) : (
            <span className="home-agenda__title">{title}</span>
          )}
          {kind ? <span className="home-agenda__kind">{kind}</span> : null}
        </span>
      </span>
    </li>
  </>
);
